import { Box, Button, CircularProgress } from '@mui/material';
import { TabInfo } from '../../common/utils/interfaces';
import { useState } from 'react';
import { useQuery } from '@apollo/client';
import ToolsAndTechnologiesFilter from './tools-and-technologies-filter';
import ToolsAndTechnologiesGrid from './tools-and-technology-grid';
import AddToolsAndTechForm from './forms/add-tools-and-tech-form';
import AddIcon from '@mui/icons-material/Add';
import { GET_SKILLS } from '../gql';
import { useAppSelector } from '../../../redux/hooks';
import TabsBarForTools from './tabs-bar-for-tools';
import Layout from '../../common/layout';
import { ToolsAndTechnologyFilters, Tool } from '../type';

const tabs: TabInfo[] = [
  { label: 'SE', value: 'SE' },
  { label: 'QA', value: 'QA' },
  { label: 'BA', value: 'BA' },
  { label: 'PM', value: 'PM' },
  { label: 'DevOps', value: 'Devops' }
];

function ToolsAndTechnologies(): JSX.Element {
  const profileData = useAppSelector((state) => state.profileData.profileData);

  const { refetch, loading, data: skillsData } = useQuery(GET_SKILLS);

  const [tab, setTab] = useState('SE');

  const [toolsAndTechnologyFilters, setToolsAndTechnologyFilters] =
    useState<ToolsAndTechnologyFilters>({ name: '' });

  const [isAddToolsFormVisible, setAddToolsFormVisible] = useState(false);

  const permissions = profileData?.permissions;

  const handleTabChange = (
    _event: React.SyntheticEvent,
    newTab: string
  ): void => {
    setTab(newTab);
  };

  const createToolCallback = (): void => {
    void refetch();
  };

  const handleScroll = (event: React.UIEvent<HTMLElement>): void => {
    const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;

    if (scrollHeight - scrollTop <= clientHeight + 1) {
      const currentLength: number = skillsData.getSkills.length;
      void (async () => {
        await refetch({
          pagination: {
            skip: 0,
            take: 23 + currentLength
          }
        });
      })();
    }
  };

  const handleFilterChange = (newFilters: ToolsAndTechnologyFilters): void => {
    setToolsAndTechnologyFilters(newFilters);
  };

  const filteredTools =
    skillsData?.getSkills
      .filter((tool: Tool) => {
        const matchesName = toolsAndTechnologyFilters.name
          ? tool.name
              .toLowerCase()
              .startsWith(toolsAndTechnologyFilters.name.toLowerCase())
          : true;
        const matchesTab = tab ? tool.department === tab : true;
        return matchesName && matchesTab;
      })
      .sort((a: { name: string }, b: { name: string }) =>
        a.name.localeCompare(b.name)
      ) || [];

  return (
    <Layout
      mainHeading="Tools & Technologies"
      subHeading="User Management > Tools & Technologies"
    >
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          marginTop: '20px',
          flexDirection: 'column',
          marginBottom: '10px'
        }}
      >
        <TabsBarForTools
          tabs={tabs}
          tab={tab}
          handleTabChange={handleTabChange}
        />
        <Box
          sx={{
            height: '1px',
            background: '#E0E2EC'
          }}
        />
      </Box>
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <ToolsAndTechnologiesFilter onFilterChange={handleFilterChange} />
        {permissions?.tools.create && (
          <Button
            variant="contained"
            type="submit"
            startIcon={<AddIcon />}
            onClick={() => setAddToolsFormVisible(true)}
            sx={{
              borderRadius: '100px',
              height: '40px',
              fontFamily: 'Be Vietnam Pro',
              fontSize: '14px',
              lineHeight: '20px',
              textAlign: 'center',
              fontWeight: 500,
              color: '#FFFFFF',
              backgroundColor: '#295DAB',
              textTransform: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: '130px',
              '@media (max-width: 600px)': {
                fontSize: '12px',
                padding: '0 12px',
                textWrap: 'nowrap',
                minWidth: '100px'
              }
            }}
          >
            Add New
          </Button>
        )}
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems:
            skillsData?.getSkills.length === 0 ? 'center' : 'flex-start',
          overflowY: skillsData?.getSkills.length === 0 ? 'none' : 'auto',
          height: 'calc(100vh - 330px)',
          width: '100%',
          scrollPadding: '0px',
          '&::-webkit-scrollbar': {
            width: '7px'
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(217, 217, 217, 1)',
            borderRadius: '10px'
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'rgba(217, 217, 217, 0.4)',
            borderRadius: '10px'
          },
          marginTop: '10px',
          paddingRight: {
            xs: '12px',
            sm: 0
          }
        }}
        onScroll={handleScroll}
      >
        {loading ? (
          <Box
            sx={{
              display: 'flex',
              height: '100%',
              justifyContent: 'center',
              alignItems: 'center',
              pointerEvents: 'none'
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <ToolsAndTechnologiesGrid
            toolsAndTechnologies={filteredTools}
            editToolCallback={createToolCallback}
            tabs={tabs}
            permissions={permissions}
          />
        )}
      </Box>

      <AddToolsAndTechForm
        open={isAddToolsFormVisible}
        handleClose={() => setAddToolsFormVisible(false)}
        id={toolsAndTechnologyFilters.name}
        comment=""
        defaultDepartment={tab}
        createToolCallback={createToolCallback}
        tabs={tabs}
      />
    </Layout>
  );
}

export default ToolsAndTechnologies;
